"use client";
import styles from "./CarVariantCard.module.scss";
import Button from "@components/atoms/Button";
import Text from "@components/atoms/Text";
import Image from "next/image";
import { formatCurrencyINR, formatPrice, maskAmount } from "@utils/numberFormatter";
import { CarVariantCardProps } from "./CarVariantCard.types";
import assetLoader from "@utils/internalImageLoder";
import { addCaptureIntentIfNeeded, getLoginUrl } from "@utils/getLoginUrl";
import { formatDeliveryDate } from "@utils/formatDate";
import { trackEventWithCommonProps, EventType } from "@utils/eventTracker";
import { PAGE_TYPES } from "@constants/pageTypes";
import { LoginSdkConfig } from '@enums/LoginSdkConfig';
import { handleLoginFlow } from "@utils/loginFlowHandler";
import CustomLink from "@components/atoms/Link";

export const CarVariantCard = ({
  data,
  isLoggedIn,
  compareButtonClickHandler,
  isAddedToCompare,
  makeName,
  modelName,
  isSellable,
  pageType,
  carAvailability,
  cityName,
}: CarVariantCardProps) => {
  const handleSelectVariantClick = async () => {
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: pageType || PAGE_TYPES.VARIANT_PAGE,
      additionalProps: {
        action_type: "tap_btn",
        section: "Variant",
        action_name: "tap_btn_variants_see_details",
        selection: data?.variantName,
        car_availability: carAvailability,
        vehicle_make: makeName,
        vehicle_model: modelName,
        city_name: cityName,
      },
    });
  };

  // Event tracking for check ACKO Drive price button
  const handleCheckPriceClick = async () => {
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: pageType || PAGE_TYPES.VARIANT_PAGE,
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_login_to_save",
        section: "Variant",
        variant_name: data?.variantName || "",
        car_availability: carAvailability || "",
        vehicle_make: makeName || "",
        vehicle_model: modelName || "",
        city_name: cityName,
      },
    });

    const mmvDetails = {
      make: makeName || "",
      model: modelName || "",
      variant: data?.variantName || "",
      color: "",
    };
    if (typeof window !== "undefined") {
      sessionStorage.setItem("loginType", "Reveal price");
      localStorage.setItem(
        "MMV",
        JSON.stringify({
          make: makeName || "",
          model: modelName || "",
          variant: data?.variantName || "",
        })
      );
    }
    const loginUrl = getLoginUrl(
      addCaptureIntentIfNeeded(
        window.location.href,
        mmvDetails,
        "Reveal price",
        pageType
      )
    );
    await handleLoginFlow(loginUrl, LoginSdkConfig.AD_PRICE);
  };

  const isDiscontinued = data.status === "out_of_production";
  const nonSellable = data.status === "non_sellable";

  return (
    <div className={styles.container}>
      <div className={styles.badgeWrapper}>
        {data?.variantLevel && data?.variantLevel !== "OTHERS" && (
          <Text
            className={`${styles["top-selling"]} ${
              styles[data.variantLevel.toLowerCase().replace("_", "-")]
            }`}
          >
            {data.variantLevel.replace("_", " ").toLowerCase()}
          </Text>
        )}
      </div>

      <div className={styles.header}>
        <div>
          <CustomLink
            href={`/cars/${data?.slug}`}
            className={styles.variantNameLink}
          >
            <Text level="h2" className={styles.variantName}>
              {data?.variantName}
            </Text>
          </CustomLink>
          <Text className={styles.variantType}>
            {`${data?.fuelType} • ${data?.transmission}`}
          </Text>
        </div>
        {isSellable ? (
          <div className={styles.priceInfo}>
            <div className={styles.priceContainer}>
              {isLoggedIn && (
                <Text className={styles.price}>
                  {`${formatPrice(data?.onRoadPrice)}`}
                </Text>
              )}
              <Text className={styles.ackoPrice}>
                {`${formatPrice(
                  isLoggedIn ? data?.adspPrice : data?.onRoadPrice
                )}`}
              </Text>
            </div>
            {data?.ackoDriveSavings && data?.ackoDriveSavings !== 0 ? (
              <Text className={styles.savings}>
                {`Save up to ${
                  isLoggedIn
                    ? `${formatCurrencyINR(data?.ackoDriveSavings)}`
                    : maskAmount(data?.ackoDriveSavings)
                }`}
              </Text>
            ) : null}
          </div>
        ) : (
          <div className={styles.priceInfo}>
            <Text className={styles.ackoPrice}>
              {`${formatPrice(data?.onRoadPrice)}`}
            </Text>
          </div>
        )}
      </div>

      <div className={styles.content}>
        {!isSellable && (
          <div className={styles.discontinuedBadge}>
            {isDiscontinued && (
              <>
                <Image
                  src={`${assetLoader()}/icons/close-dark.svg`}
                  alt="Discontinued"
                  width={14}
                  height={14}
                  className={styles.closeIcon}
                />
                <Text className={styles.discontinuedText}>Discontinued</Text>
              </>
            )}
            {nonSellable && (
              <>
                <Image
                  src={`${assetLoader()}/icons/exclamation-dark.svg`}
                  alt="Unavailable"
                  width={16}
                  height={16}
                  className={styles.closeIcon}
                />
                <Text className={styles.discontinuedText}>
                  Unavailable on ACKO Drive
                </Text>
              </>
            )}
          </div>
        )}
        {/* <div className={styles.features}>
          <Text level="h3" className={styles.featuresTitle}>
            Top features
          </Text>
          <ul className={styles.featuresList}>
            {data?.featureItemDtos?.map((feature: any, index: any) => (
              <li key={index} className={styles.featureItem}>
                <Image
                  src={`${assetLoader()}/icons/tick.svg`}
                  alt={"tick"}
                  width={16}
                  height={16}
                  loading="lazy"
                />
                {feature.name}
              </li>
            ))}
          </ul>
        </div> */}
        {isSellable &&
          (data?.expressDelivery && isLoggedIn ? (
            <div className={`${styles.expressDelivery}`}>
              <Image
                src={`${assetLoader()}/icons/ExpressDelivery.svg`}
                alt={"Express delivery"}
                className={styles.chevronDown}
                width={16}
                height={16}
                loading="lazy"
              />
              <Text className={styles.expressText}>
                {data?.expressDeliveryDays
                  ? `Express delivery by ${formatDeliveryDate(
                      parseInt(data?.expressDeliveryDays)
                    )}`
                  : "Express delivery"}
              </Text>
            </div>
          ) : (
            <div className={`${styles.deliveryBadge}`}>
              <Text className={styles.deliveryText}>
                {data?.standardDeliveryDays
                  ? `Standard delivery by ${formatDeliveryDate(
                      parseInt(data?.standardDeliveryDays)
                    )}`
                  : `Standard delivery`}
              </Text>
            </div>
          ))}

        <div className={styles.actionsContainer}>
          <div className={styles.actions}>
            <Button
              className={`${styles.compareButton} ${
                isAddedToCompare ? styles.addedToCompareButton : ""
              } ${isLoggedIn ? styles.loggedInButton : styles.loggedoutButton}`}
              onButtonClick={compareButtonClickHandler}
              ariaLabel="Add to Compare"
            >
              {isAddedToCompare ? (
                <>
                  <Image
                    src={`${assetLoader()}/icons/IconCheck.svg`}
                    alt="tick"
                    width={20}
                    height={20}
                    loading="lazy"
                  />
                  <Text>Added</Text>
                </>
              ) : (
                "Add to Compare"
              )}
            </Button>
            <Button
              className={`${styles.exploreButton} ${
                isLoggedIn ? styles.loggedInExploreButton : ""
              } ${isLoggedIn ? styles.loggedInButton : styles.loggedoutButton}`}
              link={`${process.env.NEXT_PUBLIC_BASE_TERM}/cars/${data?.slug}`}
              ariaLabel="Select Variant"
              onButtonClick={handleSelectVariantClick}
            >
              {isSellable ? "Select Variant" : "Explore Variant"}
            </Button>
          </div>

          {!isLoggedIn && (
            <>
              <Button
                ariaLabel="Check ACKO Drive price"
                className={`${styles.checkPriceButton} ${
                  isSellable ? "" : styles.checkPriceUnavailableButton
                }`}
                onClick={handleCheckPriceClick}
              >
                {isSellable
                  ? "Check ACKO Drive price"
                  : "Unavailable on ACKO Drive"}
              </Button>
              {data?.expressDelivery && (
                <div className={styles.expressDeliveryText}>
                  <Image
                    src={`${assetLoader()}/icons/ExpressDelivery.svg`}
                    alt={"Express delivery"}
                    className={styles.chevronDown}
                    width={16}
                    height={16}
                    loading="lazy"
                  />
                  Express delivery available
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
